import { getErrorI18nKey } from '@/utils/errorCodeMap'
import platformManager from '@/service/PlatformManager'

/**
 * 业务错误码 / 错误文案处理（当前供 request 拦截器使用，后续可继续扩展）。
 *
 * 解析优先级：
 * 1. ERROR_MESSAGE_HANDLERS 中的专用处理器（可定制文案、后续可扩展行为）
 * 2. errorCodeMap 中的 i18n key
 * 3. 后端 fallback 文案
 * 4. 默认 serviceUnavailable
 */

export type ErrorCode = number | string | undefined

/** 将错误码规范为 number；无法解析时返回 null */
export const toNumericErrorCode = (code: ErrorCode): number | null => {
  if (typeof code === 'number' && Number.isFinite(code)) {
    return code
  }
  if (code == null || code === '') {
    return null
  }
  const numericCode = Number(code)
  return Number.isNaN(numericCode) ? null : numericCode
}

/** 按平台返回店铺未绑定提示文案：ud → USADrop，wiio / 默认 → Wiio */
const getStoreBoundConfirmMessage = (): string => {
  platformManager.prehandle()
  const from = String(platformManager.getForm() || '').toLowerCase()
  if (from === 'ud') {
    return window.$t('confirmStoreConnectedUsaDrop') as string
  }
  return window.$t('confirmStoreConnectedWiio') as string
}

/**
 * 特殊错误码处理器：可覆盖默认映射文案，后续也可在此扩展更多逻辑。
 * key 为业务错误码，value 返回最终展示文案。
 */
export const ERROR_MESSAGE_HANDLERS: Record<number, () => string> = {
  // 店铺未绑定 Dropshipping（按平台区分文案）
  42008: getStoreBoundConfirmMessage
}

/**
 * 根据错误码获取错误消息
 * @param code 错误码
 * @param fallbackMsg 后端返回的原始消息（msg/message/debugMsg）
 */
export const getErrorMessage = (
  code: ErrorCode,
  fallbackMsg?: string
): string => {
  const numericCode = toNumericErrorCode(code)

  if (numericCode != null) {
    const specialHandler = ERROR_MESSAGE_HANDLERS[numericCode]
    if (specialHandler) {
      return specialHandler()
    }

    const i18nKey = getErrorI18nKey(numericCode)
    if (i18nKey) {
      return window.$t(i18nKey) as string
    }
  }

  return fallbackMsg || (window.$t('serviceUnavailable') as string)
}
